import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { ITINERARY_STATE } from 'itrvl-types';
import { useApi } from 'common/hooks/api';

const STARTS_WITHIN = 90;
const STARTS_WITHIN_UNIT = 'day';
const VIEWED_WITHIN = 2;
const VIEWED_WITHIN_UNIT = 'week';
const ITINERARY_LIMIT = 5;
const NOTIFICATION_LIMIT = 5;

export const STARTS_WITHIN_PHRASE = `${STARTS_WITHIN} ${STARTS_WITHIN_UNIT}${STARTS_WITHIN > 1 ? 's' : ''}`;
export const VIEWED_WITHIN_PHRASE = `${VIEWED_WITHIN} ${VIEWED_WITHIN_UNIT}${VIEWED_WITHIN > 1 ? 's' : ''}`;

export const dashboardKeys = {
  agents: agencyId => ['dashboard', 'agents', agencyId],
  upcomingItineraries: (agencyId, agentId) => ['dashboard', 'upcomingItineraries', agencyId, agentId],
  recentItineraries: (agencyId, agentId) => ['dashboard', 'recentItineraries', agencyId, agentId],
  notifications: (agencyId, agentId) => ['dashboard', 'notifications', agencyId, agentId],
};

export const useAgencyAgentsQuery = (agencyId, { enabled = true } = {}) => {
  const Api = useApi();

  return useQuery({
    queryKey: dashboardKeys.agents(agencyId),
    queryFn: async () => {
      const { data: agents = [] } = await Api.getAgencyAgents(agencyId);
      return agents;
    },
    enabled: enabled && !!agencyId,
  });
};

export const useUpcomingItinerariesQuery = (agencyId, agentId) => {
  const Api = useApi();

  return useQuery({
    queryKey: dashboardKeys.upcomingItineraries(agencyId, agentId),
    queryFn: async () => {
      const now = moment();
      const NOW_DATE = now.format('YYYY-MM-DD');
      const START_DATE = now.add(STARTS_WITHIN, `${STARTS_WITHIN_UNIT}s`).format('YYYY-MM-DD');
      const agentFilter = agentId === agencyId ? { agencyId } : { agentId };
      const where = {
        and: [
          { ...agentFilter },
          { 'itinerary.startDate': { gte: NOW_DATE } },
          { 'itinerary.startDate': { lte: START_DATE } },
          { 'itinerary.state': { inq: [ITINERARY_STATE.CONFIRMED, ITINERARY_STATE.CONFIRM_REQUESTED] } },
          { or: [{ savedAs: { exists: false } }, { savedAs: null }] },
        ],
      };
      const filter = {
        where,
        limit: ITINERARY_LIMIT,
        order: 'itinerary.startDate ASC',
        include: { client: ['agency', 'agent'] },
      };
      const { data: itineraries } = await Api.getItineraries(filter);
      return itineraries;
    },
    enabled: !!agencyId && !!agentId,
  });
};

export const useRecentItinerariesQuery = (agencyId, agentId) => {
  const Api = useApi();

  return useQuery({
    queryKey: dashboardKeys.recentItineraries(agencyId, agentId),
    queryFn: async () => {
      const LAST_VIEWED_DATE = moment().subtract(VIEWED_WITHIN, `${VIEWED_WITHIN_UNIT}s`).toDate();
      const agentFilter = agentId === agencyId ? { agencyId } : { agentId };
      const where = {
        and: [{ ...agentFilter }, { updatedDate: { gte: LAST_VIEWED_DATE } }, { or: [{ savedAs: { exists: false } }, { savedAs: null }] }],
      };
      const filter = {
        where,
        limit: ITINERARY_LIMIT,
        order: 'updatedDate DESC',
        include: { client: ['agency', 'agent'] },
      };
      const { data: itineraries } = await Api.getItineraries(filter);
      return itineraries;
    },
    enabled: !!agencyId && !!agentId,
  });
};

export const useClientNotificationsQuery = (agencyId, agentId) => {
  const Api = useApi();

  return useQuery({
    queryKey: dashboardKeys.notifications(agencyId, agentId),
    queryFn: async () => {
      const params = { limit: NOTIFICATION_LIMIT, order: 'timestamp DESC' };
      const { data: notifications = [] } =
        agentId === agencyId
          ? await Api.getClientNotificationsForAgency(agencyId, params)
          : await Api.getClientNotificationsForAgent(agentId, params);
      return notifications;
    },
    enabled: !!agencyId && !!agentId,
  });
};
